import { useEffect, useMemo, useState } from 'react';
import type {
  AccountDto,
  CatalogItemDto,
  CustomerDto,
  DebtsReportResponse,
  PurchasesAnalysisResponse,
  SalesAnalysisResponse,
  SupplierDto,
  TabularReportResponse,
  TreasuryAccountsResponse,
  TreasuryMovementResponse,
} from '@hesabat/contracts';
import { Money } from '@hesabat/finance-core';
import { REPORT_COLUMNS } from '@hesabat/contracts';
import { SOURCE_LABELS } from './AccountLedger';
import { api, ProblemError, UnauthorizedError } from './api';
import { downloadCsv } from './csv';
import { formatMoney, isZero } from './money';
import { useCompanyIdentity } from './PrintDocument';
import ReportActions from './ReportActions';
import {
  analysisCsv,
  CATALOG,
  cellText,
  isTabular,
  partyOf,
  tabularCsv,
  type TabularReport,
  centerEntry,
  DEBTS_COLUMNS,
  debtsCsv,
  defaultFilters,
  groupLabel,
  loadFilters,
  PURCHASES_COLUMNS,
  SALES_COLUMNS,
  saveFilters,
  STATUS_LABELS,
  storageKey,
  TREASURY_CATEGORY_LABELS,
  TREASURY_COLUMNS,
  treasuryCsv,
  type CatalogTarget,
  type CenterFilters,
  type CenterReport,
} from './centerReports';
import Hint from './Hint';

interface Props {
  companyId: string;
  userId: string;
  currency: string;
  onOpenLedger: (accountCode: string) => void;
  onOpenCustomer: (customerId: string) => void;
  onOpenSupplier: (supplierId: string) => void;
  /** فتح تقرير قائم فى شاشته من الفهرس. */
  onNavigate: (target: CatalogTarget) => void;
  /** سجل التدقيق لمن يدير الأعضاء وحده — يُخفى من الفهرس لغيره (والـ API يرفض على أى حال). */
  canReadAuditLog: boolean;
  /** فتح تقرير بعينه بفلاتره (من اللوحة مثلاً) بدل الفهرس. */
  initial?: Partial<CenterFilters>;
  onSessionLost: () => void;
}

type Result =
  | { report: 'sales'; data: SalesAnalysisResponse }
  | { report: 'purchases'; data: PurchasesAnalysisResponse }
  | { report: 'treasury'; data: TreasuryMovementResponse }
  | { report: 'debts'; data: DebtsReportResponse }
  | { report: TabularReport; data: TabularReportResponse };

function today(): string {
  const now = new Date();
  return (
    now.getFullYear() +
    '-' +
    String(now.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(now.getDate()).padStart(2, '0')
  );
}

/** القوائم المساعدة للتصفية — من لا يملك صلاحية قائمة يرى التقرير بلا تلك التصفية. */
interface Lookups {
  customers: CustomerDto[];
  suppliers: SupplierDto[];
  items: CatalogItemDto[];
  accounts: AccountDto[];
  treasury: TreasuryAccountsResponse | null;
}

/**
 * مركز التقارير — شاشة واحدة يختار فيها المالك التقرير والفترة والتصفية والتجميع، ويرى
 * الإجماليات، ويطبع أو يصدّر. الأرقام من الـ API كما هى (محسوبة فى SQL)؛ الواجهة تعرض
 * فقط. آخر اختيارات تُحفظ لكل مستخدم وشركة فى هذا المتصفح.
 */
export default function ReportsCenter({
  companyId,
  userId,
  currency,
  onOpenLedger,
  onOpenCustomer,
  onOpenSupplier,
  onNavigate,
  canReadAuditLog,
  initial,
  onSessionLost,
}: Props) {
  const key = storageKey(userId, companyId);
  const [filters, setFilters] = useState<CenterFilters>(() => ({
    ...loadFilters(key, defaultFilters(today())),
    ...initial,
  }));
  // الفهرس أولاً؛ فتح تقرير يستعيد آخر اختياراته المحفوظة — إلا إن جاء تقرير بعينه
  const [open, setOpen] = useState(initial?.report !== undefined);
  const [result, setResult] = useState<Result | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lookups, setLookups] = useState<Lookups>({
    customers: [],
    suppliers: [],
    items: [],
    accounts: [],
    treasury: null,
  });
  const identity = useCompanyIdentity(companyId, onSessionLost);

  useEffect(() => saveFilters(key, filters), [key, filters]);

  const set = <K extends keyof CenterFilters>(field: K, value: CenterFilters[K]) =>
    setFilters((current) => ({ ...current, [field]: value }));

  useEffect(() => {
    let alive = true;
    const quiet = <T,>(promise: Promise<T>, fallback: T) =>
      promise.catch((caught: unknown) => {
        if (caught instanceof UnauthorizedError) onSessionLost();
        return fallback;
      });
    void Promise.all([
      quiet(api.customers(companyId), []),
      quiet(api.suppliers(companyId), []),
      quiet(api.catalogItems(companyId), []),
      quiet(api.accounts(companyId), []),
      quiet(api.treasuryAccounts(companyId), null),
    ]).then(([customers, suppliers, items, accounts, treasury]) => {
      if (alive) setLookups({ customers, suppliers, items, accounts, treasury });
    });
    return () => {
      alive = false;
    };
  }, [companyId, onSessionLost]);

  const f = filters;
  const periodInvalid = f.report !== 'debts' && f.from > f.to;
  const minInvalid = f.minBalance !== '' && !/^\d{1,16}(\.\d{1,4})?$/.test(f.minBalance);
  const daysInvalid =
    f.report === 'slow-moving' &&
    !(/^\d{1,4}$/.test(f.days) && Number(f.days) >= 1 && Number(f.days) <= 3650);
  // تقارير فى تاريخ واحد هو نهاية الفترة
  const singleDate = f.report === 'unapplied-receipts' || f.report === 'slow-moving';

  useEffect(() => {
    if (!open || periodInvalid || minInvalid || daysInvalid) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const period = { from: f.from, to: f.to };
    const tabular = f.report;
    const load: Promise<Result> = isTabular(tabular)
      ? (tabular === 'audit-log'
          ? api.auditLog(companyId, {
              ...period,
              action: f.action,
              entityType: f.entityType,
            })
          : api.catalogReport(companyId, tabular, {
              ...(singleDate ? { from: f.to, to: f.to } : period),
              partyId:
                partyOf(tabular) === 'supplier'
                  ? f.supplierId
                  : partyOf(tabular) === 'customer'
                    ? f.customerId
                    : undefined,
              status: f.status,
              noteKind: f.noteKind,
              sourceType: f.sourceType,
              limit: f.limit,
              itemId: tabular === 'stock-movement' ? f.itemId : undefined,
              days: tabular === 'slow-moving' ? f.days : undefined,
            })
        ).then((data) => ({ report: tabular, data }))
      : f.report === 'sales'
        ? api
            .salesAnalysis(companyId, {
              ...period,
              groupBy: f.salesGroupBy,
              customerId: f.customerId,
              itemId: f.itemId,
              status: f.status,
            })
            .then((data) => ({ report: 'sales', data }))
        : f.report === 'purchases'
          ? api
              .purchasesAnalysis(companyId, {
                ...period,
                groupBy: f.purchasesGroupBy,
                supplierId: f.supplierId,
                accountCode: f.accountCode,
                status: f.status,
              })
              .then((data) => ({ report: 'purchases', data }))
          : f.report === 'treasury'
            ? api
                .treasuryMovement(companyId, { ...period, accountCode: f.treasuryAccountCode })
                .then((data) => ({ report: 'treasury', data }))
            : api
                .debtsReport(companyId, {
                  party: f.party,
                  asOf: f.asOf,
                  overdueOnly: String(f.overdueOnly),
                  minBalance: f.minBalance,
                  sort: f.sort,
                })
                .then((data) => ({ report: 'debts', data }));

    load
      .then((loaded) => {
        if (!cancelled) setResult(loaded);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof UnauthorizedError) {
          onSessionLost();
          return;
        }
        setResult(null);
        setError(
          caught instanceof ProblemError
            ? (caught.problem.detail ?? caught.problem.title)
            : caught instanceof Error
              ? caught.message
              : String(caught),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // كل حقل يغيّر الاستعلام يعيد التحميل؛ تغيير التقرير وحده يكفى لاختيار المسار
  }, [
    companyId,
    onSessionLost,
    open,
    periodInvalid,
    minInvalid,
    daysInvalid,
    singleDate,
    f.report,
    f.from,
    f.to,
    f.asOf,
    f.salesGroupBy,
    f.purchasesGroupBy,
    f.status,
    f.customerId,
    f.itemId,
    f.supplierId,
    f.accountCode,
    f.treasuryAccountCode,
    f.party,
    f.overdueOnly,
    f.minBalance,
    f.sort,
    f.noteKind,
    f.sourceType,
    f.limit,
    f.action,
    f.entityType,
    f.days,
  ]);

  // النتيجة المعروضة تخص التقرير المختار فقط — لا تظهر أرقام تقرير سابق تحت عنوان جديد
  const shown = result && result.report === f.report ? result : null;

  const treasuryOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const entity of [
      ...(lookups.treasury?.cashboxes ?? []),
      ...(lookups.treasury?.bankAccounts ?? []),
    ]) {
      seen.set(
        entity.accountCode,
        (seen.has(entity.accountCode) ? seen.get(entity.accountCode) + '، ' : '') + entity.nameAr,
      );
    }
    return [...seen.entries()];
  }, [lookups.treasury]);

  const { category, entry } = centerEntry(f.report);
  const title = entry.title;
  const subtitle =
    f.report === 'debts'
      ? (f.party === 'customers' ? 'العملاء' : 'الموردون') + ' فى ' + f.asOf
      : f.report === 'unapplied-receipts'
        ? 'فى ' + f.to
        : 'من ' + f.from + ' إلى ' + f.to;

  function exportCurrent(): void {
    if (!shown) return;
    const name = title.replace(/\s+/g, '-') + '-' + subtitle.replace(/\s+/g, '-');
    const csv =
      shown.report === 'sales'
        ? analysisCsv(shown.data, SALES_COLUMNS)
        : shown.report === 'purchases'
          ? analysisCsv(shown.data, PURCHASES_COLUMNS)
          : shown.report === 'treasury'
            ? treasuryCsv(shown.data)
            : shown.report === 'debts'
              ? debtsCsv(shown.data)
              : tabularCsv(shown.data, SOURCE_LABELS);
    downloadCsv(name + '.csv', csv.header, csv.rows);
  }

  if (!open) {
    return (
      <Catalog
        onOpen={(report) => {
          set('report', report);
          setOpen(true);
        }}
        onNavigate={onNavigate}
        canReadAuditLog={canReadAuditLog}
      />
    );
  }

  return (
    <div className="panel reports-center">
      <div className="print-only report-letterhead">
        {identity?.logoUrl && <img src={identity.logoUrl} alt="" />}
        <div>
          <strong>{identity?.profile.legalNameAr ?? ''}</strong>
          <div>
            {title} — {subtitle}
          </div>
        </div>
      </div>

      <div className="panel-head">
        <h2>مركز التقارير</h2>
        {!loading && shown && <ReportActions onExport={exportCurrent} />}
      </div>

      <p className="no-print breadcrumb">
        <button type="button" className="link" onClick={() => setOpen(false)}>
          كل التقارير
        </button>{' '}
        / {category.title} / <strong>{title}</strong>
      </p>
      <p className="sub no-print">{entry.hint}</p>

      <div className="form-row no-print">
        {f.report === 'debts' ? (
          <>
            <label>
              الطرف
              <select
                value={f.party}
                onChange={(event) => set('party', event.target.value as CenterFilters['party'])}
              >
                <option value="customers">العملاء (لنا عندهم)</option>
                <option value="suppliers">الموردون (لهم عندنا)</option>
              </select>
            </label>
            <label>
              فى تاريخ
              <input
                type="date"
                value={f.asOf}
                onChange={(event) => set('asOf', event.target.value)}
              />
            </label>
            <label>
              أقل رصيد
              <input
                inputMode="decimal"
                value={f.minBalance}
                placeholder="الكل"
                onChange={(event) => set('minBalance', event.target.value.trim())}
              />
              <Hint>أخفِ الأرصدة الأصغر من هذا المبلغ لترى المهم فقط.</Hint>
            </label>
            <label>
              الترتيب
              <select
                value={f.sort}
                onChange={(event) => set('sort', event.target.value as CenterFilters['sort'])}
              >
                <option value="largest">الأكبر رصيدًا أولاً</option>
                <option value="overdue">الأكثر تأخرًا أولاً</option>
                <option value="code">بالكود</option>
              </select>
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={f.overdueOnly}
                onChange={(event) => set('overdueOnly', event.target.checked)}
              />
              المتأخر فقط
            </label>
          </>
        ) : singleDate ? (
          <label>
            فى تاريخ
            <input type="date" value={f.to} onChange={(event) => set('to', event.target.value)} />
          </label>
        ) : (
          <>
            <label>
              من
              <input
                type="date"
                value={f.from}
                onChange={(event) => set('from', event.target.value)}
              />
            </label>
            <label>
              إلى
              <input type="date" value={f.to} onChange={(event) => set('to', event.target.value)} />
            </label>
          </>
        )}

        {isTabular(f.report) && partyOf(f.report) === 'customer' && (
          <label>
            العميل
            <select
              value={f.customerId}
              onChange={(event) => set('customerId', event.target.value)}
            >
              <option value="">كل العملاء</option>
              {lookups.customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.code} — {customer.nameAr}
                </option>
              ))}
            </select>
          </label>
        )}
        {isTabular(f.report) &&
          partyOf(f.report) === 'supplier' &&
          f.report !== 'top-suppliers' && (
            <label>
              المورد
              <select
                value={f.supplierId}
                onChange={(event) => set('supplierId', event.target.value)}
              >
                <option value="">كل الموردين</option>
                {lookups.suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.code} — {supplier.nameAr}
                  </option>
                ))}
              </select>
            </label>
          )}
        {(f.report === 'sales-notes' || f.report === 'purchase-notes') && (
          <label>
            نوع الإشعار
            <select
              value={f.noteKind}
              onChange={(event) => set('noteKind', event.target.value as CenterFilters['noteKind'])}
            >
              <option value="all">الكل</option>
              <option value="credit">دائن</option>
              <option value="debit">مدين</option>
            </select>
          </label>
        )}
        {(f.report === 'top-customers' || f.report === 'top-suppliers') && (
          <label>
            العدد
            <select value={f.limit} onChange={(event) => set('limit', event.target.value)}>
              {['5', '10', '20', '50', '100'].map((count) => (
                <option key={count} value={count}>
                  أكبر {count}
                </option>
              ))}
            </select>
          </label>
        )}
        {f.report === 'journal-register' && (
          <label>
            المصدر
            <select
              value={f.sourceType}
              onChange={(event) => set('sourceType', event.target.value)}
            >
              <option value="">كل المصادر</option>
              {Object.entries(SOURCE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        )}
        {f.report === 'stock-movement' && (
          <label>
            الصنف
            <select value={f.itemId} onChange={(event) => set('itemId', event.target.value)}>
              <option value="">كل الأصناف المخزنية</option>
              {lookups.items
                .filter((item) => item.kind === 'stock')
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.nameAr}
                  </option>
                ))}
            </select>
          </label>
        )}
        {f.report === 'slow-moving' && (
          <label>
            بلا بيع منذ (يوم)
            <input
              inputMode="numeric"
              value={f.days}
              onChange={(event) => set('days', event.target.value.trim())}
            />
            <Hint>أصناف لم يُبع منها شىء خلال هذه المدة — بضاعة راكدة تحبس فلوسك.</Hint>
          </label>
        )}
        {f.report === 'audit-log' && (
          <>
            <label>
              الإجراء
              <input
                value={f.action}
                placeholder="مثل sales_invoice.create"
                onChange={(event) => set('action', event.target.value.trim())}
              />
            </label>
            <label>
              نوع السجل
              <input
                value={f.entityType}
                placeholder="مثل sales_invoice"
                onChange={(event) => set('entityType', event.target.value.trim())}
              />
            </label>
          </>
        )}

        {f.report === 'sales' && (
          <>
            <label>
              التجميع
              <select
                value={f.salesGroupBy}
                onChange={(event) =>
                  set('salesGroupBy', event.target.value as CenterFilters['salesGroupBy'])
                }
              >
                <option value="month">بالشهر</option>
                <option value="day">باليوم</option>
                <option value="customer">بالعميل</option>
                <option value="item">بالصنف</option>
              </select>
              <Hint>كيف تُجمَّع الأرقام فى التقرير: حسب الطرف، الصنف، أو الشهر.</Hint>
            </label>
            <label>
              العميل
              <select
                value={f.customerId}
                onChange={(event) => set('customerId', event.target.value)}
              >
                <option value="">كل العملاء</option>
                {lookups.customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.code} — {customer.nameAr}
                  </option>
                ))}
              </select>
            </label>
            <label>
              الصنف
              <select value={f.itemId} onChange={(event) => set('itemId', event.target.value)}>
                <option value="">كل الأصناف</option>
                {lookups.items.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.nameAr}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}

        {f.report === 'purchases' && (
          <>
            <label>
              التجميع
              <select
                value={f.purchasesGroupBy}
                onChange={(event) =>
                  set('purchasesGroupBy', event.target.value as CenterFilters['purchasesGroupBy'])
                }
              >
                <option value="month">بالشهر</option>
                <option value="day">باليوم</option>
                <option value="supplier">بالمورد</option>
                <option value="account">بالبند (حساب المصروف أو الأصل)</option>
              </select>
              <Hint>كيف تُجمَّع الأرقام فى التقرير: حسب الطرف، الصنف، أو الشهر.</Hint>
            </label>
            <label>
              المورد
              <select
                value={f.supplierId}
                onChange={(event) => set('supplierId', event.target.value)}
              >
                <option value="">كل الموردين</option>
                {lookups.suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.code} — {supplier.nameAr}
                  </option>
                ))}
              </select>
            </label>
            <label>
              البند
              <select
                value={f.accountCode}
                onChange={(event) => set('accountCode', event.target.value)}
              >
                <option value="">كل البنود</option>
                {lookups.accounts
                  .filter((account) => ['expense', 'asset'].includes(account.accountClass))
                  .map((account) => (
                    <option key={account.code} value={account.code}>
                      {account.code} — {account.nameAr}
                    </option>
                  ))}
              </select>
            </label>
          </>
        )}

        {(f.report === 'sales' ||
          f.report === 'purchases' ||
          f.report === 'sales-invoices' ||
          f.report === 'purchase-bills') && (
          <label>
            حال المستند
            <select
              value={f.status}
              onChange={(event) => set('status', event.target.value as CenterFilters['status'])}
            >
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <Hint>المسودة لم تؤثر على الحسابات بعد؛ المُرحَّل دخل الدفاتر.</Hint>
          </label>
        )}

        {f.report === 'treasury' && (
          <label>
            الخزينة أو البنك
            <select
              value={f.treasuryAccountCode}
              onChange={(event) => set('treasuryAccountCode', event.target.value)}
            >
              <option value="">كل الخزائن والبنوك</option>
              {treasuryOptions.map(([code, names]) => (
                <option key={code} value={code}>
                  {code} — {names}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {periodInvalid && <p className="error">تاريخ البداية بعد تاريخ النهاية.</p>}
      {minInvalid && <p className="error">أقل رصيد يُكتب رقمًا موجبًا، مثل 5000 أو 1250.50.</p>}
      {daysInvalid && <p className="error">عدد الأيام رقم صحيح من 1 إلى 3650، مثل 90.</p>}
      {loading && <p className="sub">جارى التحميل…</p>}
      {error && <p className="error">{error}</p>}

      {!loading && shown && isTabular(shown.report) && (
        <TabularView
          report={shown.data as TabularReportResponse}
          onOpenCustomer={onOpenCustomer}
          onOpenSupplier={onOpenSupplier}
          onOpenLedger={onOpenLedger}
        />
      )}
      {!loading && shown?.report === 'sales' && (
        <SalesView report={shown.data} onOpenCustomer={onOpenCustomer} />
      )}
      {!loading && shown?.report === 'purchases' && (
        <PurchasesView report={shown.data} onOpenSupplier={onOpenSupplier} />
      )}
      {!loading && shown?.report === 'treasury' && (
        <TreasuryView report={shown.data} onOpenLedger={onOpenLedger} />
      )}
      {!loading && shown?.report === 'debts' && (
        <DebtsView
          report={shown.data}
          onOpenParty={shown.data.party === 'customers' ? onOpenCustomer : onOpenSupplier}
        />
      )}

      <p className="label">المبالغ بـ {currency}، من المستندات والقيود المنشورة وحدها.</p>
    </div>
  );
}

/**
 * فهرس التقارير بفئاته: المبنى هنا يُفتح فى نفس الشاشة، والقائم يُفتح فى شاشته، والمخطط
 * يظهر «قريبًا» حتى يُبنى، والمعلق على قرار محاسبى يعرض سؤاله للمالك.
 */
function Catalog({
  onOpen,
  onNavigate,
  canReadAuditLog,
}: {
  onOpen: (report: CenterReport) => void;
  onNavigate: (target: CatalogTarget) => void;
  canReadAuditLog: boolean;
}) {
  return (
    <div className="panel reports-center">
      <div className="panel-head">
        <div>
          <h2>مركز التقارير</h2>
          <p className="sub">اختر التقرير، ثم الفترة والتصفية والتجميع. كل تقرير يُطبع ويُصدَّر.</p>
        </div>
      </div>
      {CATALOG.map((category) => (
        <section key={category.id} className="catalog-category">
          <h3>{category.title}</h3>
          <div className="report-picker">
            {category.entries
              .filter(
                (entry) =>
                  canReadAuditLog || !(entry.kind === 'center' && entry.report === 'audit-log'),
              )
              .map((entry) => {
                const disabled = entry.kind === 'planned' || entry.kind === 'blocked';
                return (
                  <button
                    key={entry.id}
                    type="button"
                    className={'report-choice' + (disabled ? ' disabled' : '')}
                    disabled={disabled}
                    onClick={() => {
                      if (entry.kind === 'center') onOpen(entry.report);
                      else if (entry.kind === 'screen') onNavigate(entry.target);
                    }}
                  >
                    <strong>
                      {entry.title}
                      {entry.kind === 'planned' && <span className="pill">قريبًا</span>}
                      {entry.kind === 'blocked' && <span className="pill bad">ينتظر قرارك</span>}
                      {entry.kind === 'screen' && <span className="pill">فى شاشته</span>}
                    </strong>
                    <small>{entry.kind === 'blocked' ? entry.question : entry.hint}</small>
                  </button>
                );
              })}
          </div>
        </section>
      ))}
    </div>
  );
}

/**
 * أى تقرير جدولى من الفهرس: الأعمدة وأنواعها من العقد، والإجماليات والمطابقة من الخادم.
 * الصف ذو الرابط يفتح طرفه أو حسابه.
 */
function TabularView({
  report,
  onOpenCustomer,
  onOpenSupplier,
  onOpenLedger,
}: {
  report: TabularReportResponse;
  onOpenCustomer: (id: string) => void;
  onOpenSupplier: (id: string) => void;
  onOpenLedger: (code: string) => void;
}) {
  const columns = REPORT_COLUMNS[report.report as TabularReport] ?? [];
  const open = (link: TabularReportResponse['rows'][number]['link']) => {
    if (!link) return;
    if (link.kind === 'customer') onOpenCustomer(link.id);
    else if (link.kind === 'supplier') onOpenSupplier(link.id);
    else onOpenLedger(link.id);
  };
  const hasMoney = columns.some((column) => column.type === 'money');
  return (
    <>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {report.rows.map((row, index) => (
              <tr
                key={index}
                className={row.link ? 'clickable' : undefined}
                tabIndex={row.link ? 0 : undefined}
                onClick={row.link ? () => open(row.link) : undefined}
                onKeyDown={
                  row.link
                    ? (event) => {
                        if (event.key === 'Enter') open(row.link);
                      }
                    : undefined
                }
              >
                {columns.map((column) => {
                  const value = cellText(column, row.cells[column.key], SOURCE_LABELS);
                  if (column.type === 'money') {
                    return <Amount key={column.key} value={value as string | null} />;
                  }
                  if (
                    column.type === 'int' ||
                    column.type === 'percent' ||
                    column.type === 'quantity'
                  ) {
                    return (
                      <td key={column.key} className="num-cell">
                        {value === null ? '—' : column.type === 'percent' ? value + '%' : value}
                      </td>
                    );
                  }
                  return <td key={column.key}>{value ?? '—'}</td>;
                })}
              </tr>
            ))}
            {report.rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="sub">
                  لا سجلات بهذه التصفية
                </td>
              </tr>
            )}
            {hasMoney && report.rows.length > 0 && (
              <tr className="closing">
                {columns.map((column, index) =>
                  column.type === 'money' ? (
                    <Amount key={column.key} value={report.totals[column.key] ?? null} />
                  ) : (
                    <td key={column.key}>{index === 0 ? 'الإجمالى' : ''}</td>
                  ),
                )}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {report.truncated && (
        <p className="notice">
          ظهرت أول {report.rows.length} صف فقط — ضيّق الفترة أو التصفية لترى الباقى.
        </p>
      )}
      {report.reconciliation.map((line) => (
        <p key={line.label} className={isZero(line.difference) ? 'sub' : 'notice'}>
          {line.label}: {formatMoney(line.report)} مقابل {formatMoney(line.reference)}
          {isZero(line.difference)
            ? ' — مطابق.'
            : ' — الفرق ' + formatMoney(line.difference) + ' يستوجب المراجعة.'}
        </p>
      ))}
      {report.notes.map((note) => (
        <p key={note} className="sub">
          {note}
        </p>
      ))}
    </>
  );
}

function Amount({ value }: { value: string | null }) {
  return <td className="num-cell">{value === null ? '—' : formatMoney(value)}</td>;
}

type AnalysisColumns = typeof SALES_COLUMNS | typeof PURCHASES_COLUMNS;

function AnalysisTable({
  rows,
  totals,
  groupBy,
  columns,
  countKey,
  onOpen,
}: {
  rows: (Record<string, unknown> & { key: string; label: string; code: string | null })[];
  totals: Record<string, unknown>;
  groupBy: string;
  columns: AnalysisColumns;
  countKey: 'invoiceCount' | 'billCount';
  onOpen: ((key: string) => void) | null;
}) {
  // المحصّل والمتبقى يختفيان حين لا يُحسبان (التجميع أو التصفية بالسطر)
  const shown = columns.filter(([key]) => totals[key] !== null);
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>المجموعة</th>
            <th>المستندات</th>
            {shown.map(([key, label]) => (
              <th key={key}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.key}
              className={onOpen ? 'clickable' : undefined}
              tabIndex={onOpen ? 0 : undefined}
              onClick={onOpen ? () => onOpen(row.key) : undefined}
              onKeyDown={
                onOpen
                  ? (event) => {
                      if (event.key === 'Enter') onOpen(row.key);
                    }
                  : undefined
              }
            >
              <td>{groupLabel(groupBy, row)}</td>
              <td className="num-cell">{row[countKey] as number}</td>
              {shown.map(([key]) => (
                <Amount key={key} value={row[key] as string | null} />
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={shown.length + 2} className="sub">
                لا حركة فى الفترة بهذه التصفية
              </td>
            </tr>
          )}
          <tr className="closing">
            <td>الإجمالى</td>
            <td className="num-cell">{totals[countKey] as number}</td>
            {shown.map(([key]) => (
              <Amount key={key} value={totals[key] as string | null} />
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function SalesView({
  report,
  onOpenCustomer,
}: {
  report: SalesAnalysisResponse;
  onOpenCustomer: (customerId: string) => void;
}) {
  return (
    <>
      <AnalysisTable
        rows={report.rows}
        totals={report.totals}
        groupBy={report.groupBy}
        columns={SALES_COLUMNS}
        countKey="invoiceCount"
        onOpen={report.groupBy === 'customer' ? onOpenCustomer : null}
      />
      <p className="sub">
        صافى المبيعات = الفواتير − الإلغاء − الإشعارات الدائنة + الإشعارات المدينة، قبل الضريبة —
        وهو نفسه إيراد قائمة الدخل. المحصّل والمتبقى لفواتير صدرت فى الفترة حتى آخر يوم فيها.
      </p>
      {report.unreconciled !== null && (
        <p className={isZero(report.unreconciled) ? 'sub' : 'notice'}>
          إيرادات قائمة الدخل للفترة {formatMoney(report.ledgerRevenue)}
          {isZero(report.unreconciled)
            ? ' — مطابقة لصافى المبيعات.'
            : ' — منها ' +
              formatMoney(report.unreconciled) +
              ' من قيود إيراد لا تخص فاتورة أو إشعارًا (يدوية مثلاً). راجعها فى الأستاذ.'}
        </p>
      )}
    </>
  );
}

function PurchasesView({
  report,
  onOpenSupplier,
}: {
  report: PurchasesAnalysisResponse;
  onOpenSupplier: (supplierId: string) => void;
}) {
  return (
    <>
      <AnalysisTable
        rows={report.rows}
        totals={report.totals}
        groupBy={report.groupBy}
        columns={PURCHASES_COLUMNS}
        countKey="billCount"
        onOpen={report.groupBy === 'supplier' ? onOpenSupplier : null}
      />
      <p className="sub">
        صافى المشتريات = فواتير الموردين − الإلغاء − الإشعارات المدينة (مرتجع وخصم) + الإشعارات
        الدائنة، قبل الضريبة.
      </p>
    </>
  );
}

function TreasuryView({
  report,
  onOpenLedger,
}: {
  report: TreasuryMovementResponse;
  onOpenLedger: (accountCode: string) => void;
}) {
  if (report.accounts.length === 0) {
    return <p className="sub">لا خزائن ولا حسابات بنكية بعد.</p>;
  }
  return (
    <>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>الحساب</th>
              {TREASURY_COLUMNS.map(([key, label]) => (
                <th key={key}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {report.accounts.map((account) => (
              <tr
                key={account.accountCode}
                className="clickable"
                tabIndex={0}
                onClick={() => onOpenLedger(account.accountCode)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') onOpenLedger(account.accountCode);
                }}
              >
                <td>
                  {account.accountCode} — {account.holders.join('، ') || account.accountNameAr}
                </td>
                {TREASURY_COLUMNS.map(([key]) => (
                  <Amount key={key} value={account[key]} />
                ))}
              </tr>
            ))}
            {report.accounts.length > 1 && (
              <tr className="closing">
                <td>الإجمالى</td>
                {TREASURY_COLUMNS.map(([key]) => (
                  <Amount key={key} value={report.totals[key]} />
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {report.accounts.length > 1 && (
        <p className="sub">
          التحويل بين خزينتين فى التقرير يظهر واردًا وصادرًا، فيتقاص فى الإجمالى.
        </p>
      )}

      {report.accounts
        .filter((account) => account.lines.length > 0 || !isZero(account.openingBalance))
        .map((account) => (
          <section key={account.accountCode} className="treasury-account">
            <h3>
              {account.accountCode} — {account.holders.join('، ') || account.accountNameAr}
            </h3>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>التاريخ</th>
                    <th>البيان</th>
                    <th>القيد</th>
                    <th>الطرف / الوصف</th>
                    <th>وارد</th>
                    <th>منصرف</th>
                    <th>الرصيد</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="subtotal">
                    <td colSpan={6}>رصيد أول الفترة</td>
                    <Amount value={account.openingBalance} />
                  </tr>
                  {account.lines.map((line) => (
                    <tr key={line.journalId + '-' + line.lineNumber}>
                      <td>{line.postingDate}</td>
                      <td>{TREASURY_CATEGORY_LABELS[line.category]}</td>
                      <td>{line.journalNumber ?? '—'}</td>
                      <td>
                        {[line.counterparty, line.memo ?? line.reference]
                          .filter(Boolean)
                          .join(' — ') || '—'}
                      </td>
                      <Amount value={Money.of(line.debit).isZero() ? null : line.debit} />
                      <Amount value={Money.of(line.credit).isZero() ? null : line.credit} />
                      <Amount value={line.runningBalance} />
                    </tr>
                  ))}
                  <tr className="closing">
                    <td colSpan={6}>رصيد آخر الفترة</td>
                    <Amount value={account.closingBalance} />
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        ))}
    </>
  );
}

function DebtsView({
  report,
  onOpenParty,
}: {
  report: DebtsReportResponse;
  onOpenParty: (partyId: string) => void;
}) {
  const party = report.party === 'customers' ? 'العميل' : 'المورد';
  return (
    <>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>الكود</th>
              <th>{party}</th>
              {DEBTS_COLUMNS.map(([key, label]) => (
                <th key={key}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {report.rows.map((row) => (
              <tr
                key={row.partyId}
                className="clickable"
                tabIndex={0}
                title="فتح كشف الحساب"
                onClick={() => onOpenParty(row.partyId)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') onOpenParty(row.partyId);
                }}
              >
                <td>{row.code}</td>
                <td>{row.nameAr}</td>
                {DEBTS_COLUMNS.map(([key]) => (
                  <Amount key={key} value={row[key]} />
                ))}
              </tr>
            ))}
            {report.rows.length === 0 && (
              <tr>
                <td colSpan={DEBTS_COLUMNS.length + 2} className="sub">
                  لا أرصدة بهذه التصفية
                </td>
              </tr>
            )}
            <tr className="closing">
              <td colSpan={2}>الإجمالى</td>
              {DEBTS_COLUMNS.map(([key]) => (
                <Amount key={key} value={report.totals[key]} />
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="sub">
        اضغط أى صف لفتح كشف حسابه.
        {report.hiddenCount > 0 && ' أُخفى ' + report.hiddenCount + ' بالتصفية.'} إجمالى الأعمار قبل
        التصفية {formatMoney(report.agingTotal)}، ورصيد الحساب الرقابى فى الدفتر{' '}
        {formatMoney(report.controlBalance)}
        {isZero(report.unreconciled)
          ? ' — مطابق.'
          : ' — الفرق ' +
            formatMoney(report.unreconciled) +
            ' قيود لا تخص مستندًا وتستوجب المراجعة.'}
      </p>
    </>
  );
}
